import { useEffect, useState } from 'react'
import { GET_SES_EMAILS, GET_SES_EMAIL_NOTIFICATION_STATUS_COUNTS } from "@graphql"
import { useLazyQuery, useQuery } from "@apollo/client"
import ErrorLoadingPlaceholder from './ErrorLoadingPlaceholder'
import StatDetails from './StatDetails'
import StatsSummary from './StatsSummary'
import PropTypes from 'prop-types'

const NotificationDeliveryStats = ({ event, notification, statuses }) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [currentView, setCurrentView] = useState(null)
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false)
  const [isLoadingDetailedStats, setIsLoadingDetailedStats] = useState({
    is_delivered: false,
    is_failed: false,
    is_unknown: false,
    is_queued: false,
    all: false,
  })
  const [isRefetching, setIsRefetching] = useState(false)
  const [perPage] = useState(30)
  const [statusIds, setStatusIds] = useState([])
  const [view, setView] = useState("summary")

  const {data, loading, error, refetch, stopPolling} = useQuery(GET_SES_EMAIL_NOTIFICATION_STATUS_COUNTS, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    pollInterval: 60000,
    variables: {
      input: {  
        notification_id: notification.id,
      }
    }, 

    onCompleted: (data) => {
      if (data.sesEmailNotificationStatusCounts.has_completed) {
        stopPolling()
      }
    }
  })

  const [getEmails, { data: emailData, loading: sesEmailsLoading, error: sesEmailsError }] = useLazyQuery(GET_SES_EMAILS, {
    fetchPolicy: "network-only",
  })

  useEffect(() => {
    if (!loading) {
      setHasLoadedOnce(true)
    }
  }, [loading])

  const handleChangePage = (page) => {
    setCurrentPage(page)

    getEmails({
      variables: {
        perPage,
        page: page,
        input: {
          event_id: event.id,
          batch_ids: [notification.sesEmailBatch.id],
          status_ids: statusIds,
          search_term: ""
        }
      }
    })
  }

  const handleGetDetailedStats = (statusKey, newStatusIds, title, page = 1) => {
    setIsLoadingDetailedStats(prev => ({...prev, [statusKey]: true}))
    setCurrentPage(page)
    setStatusIds(newStatusIds)

    getEmails({
      variables: {
        perPage,
        page: page,
        input: {
          event_id: event.id,
          batch_ids: [notification.sesEmailBatch.id],
          status_ids: newStatusIds,
          search_term: ""
        }
      }
    })
    .then(() => {
      setView("details")
      setCurrentView(title)
    })
    .finally(() => setIsLoadingDetailedStats(prev => ({...prev, [statusKey]: false})))
  }

  const handleRefetch = () => {
    setIsRefetching(true)
    refetch().finally(() => setIsRefetching(false))
  }

  const handleSearchChange = (searchTerm) => {
    setCurrentPage(1)

    getEmails({
      variables: {
        perPage,
        page: 1,
        input: {
          event_id: event.id,
          batch_ids: [notification.sesEmailBatch.id],
          status_ids: statusIds,
          search_term: searchTerm
        }
      }
    })
  }
  
  if (error) return <ErrorLoadingPlaceholder />
    
  const stats = data?.sesEmailNotificationStatusCounts
  const isProcessing = !stats?.has_completed || false
  const sesEmails = emailData?.sesEmails?.data || []
  const paginatorInfo = emailData?.sesEmails?.paginatorInfo || {}

  return (
    <div>
      {view === "summary" && (
        <StatsSummary 
          handleGetDetailedStats={(statusKey, newStatusIds, title) => handleGetDetailedStats(statusKey, newStatusIds, title)}
          handleRefetch={handleRefetch}
          hasLoadedOnce={hasLoadedOnce}
          isLoading={loading}
          isLoadingDetailedStats={isLoadingDetailedStats} 
          isProcessing={isProcessing}
          isReady={stats?.is_ready !== undefined ? stats.is_ready : true} 
          isRefetching={isRefetching}
          stats={stats} 
          statuses={statuses}
        />
      )}

      {view === "details" && (
        <StatDetails 
          currentPage={currentPage}
          currentView={currentView}
          emails={sesEmails}
          error={sesEmailsError}
          handleClose={() => setView("summary")}
          handleChangePage={(page) => handleChangePage(page)}
          handleSearchChange={(searchTerm) => handleSearchChange(searchTerm)}
          isLoading={sesEmailsLoading}
          paginatorInfo={paginatorInfo}
          showPagination={paginatorInfo?.total > perPage}
        />
      )}
    </div>
  )
}

NotificationDeliveryStats.propTypes = {
  event: PropTypes.object.isRequired,
  notification: PropTypes.object.isRequired,
  statuses: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    is_delivered: PropTypes.bool.isRequired,
    is_failed: PropTypes.bool.isRequired,
    is_queued: PropTypes.bool.isRequired,
    is_unknown: PropTypes.bool.isRequired,
  })),
}

export default NotificationDeliveryStats